class HeroSlider {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector('.hero-slider');
    this.slides = section.querySelectorAll('.hero-slide');
    this.thumbnails = section.querySelectorAll('.thumbnail-item');
    this.dots = section.querySelectorAll('.hero-slider__dots .dot');
    this.prevButton = section.querySelector('.hero-slider__arrow--prev');
    this.nextButton = section.querySelector('.hero-slider__arrow--next');
    
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.isTransitioning = false;
    this.autoplayInterval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    
    // Settings
    this.autoplay = this.slider.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.slider.dataset.autoplaySpeed) * 1000 || 5000;
    
    this.init();
  }
  
  init() {
    if (this.totalSlides <= 1) return;
    
    // Add event listeners
    this.addEventListeners();
    
    // Start autoplay if enabled
    if (this.autoplay) {
      this.startAutoplay();
    }
    
    // Preload next image
    this.preloadImage(1);
  }
  
  addEventListeners() {
    // Arrow navigation
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previousSlide());
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }
    
    // Thumbnail navigation
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Dots navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Touch events for mobile swipe
    this.slider.addEventListener('touchstart', (e) => this.handleTouchStart(e));
    this.slider.addEventListener('touchend', (e) => this.handleTouchEnd(e));
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));
    
    // Pause autoplay on hover
    if (this.autoplay) {
      this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.slider.addEventListener('mouseleave', () => this.startAutoplay());
    }
    
    // Pause autoplay when page is not visible
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoplay();
      } else if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }
  
  previousSlide() {
    if (this.isTransitioning) return;
    const prevIndex = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
    this.goToSlide(prevIndex);
  }
  
  nextSlide() {
    if (this.isTransitioning) return;
    const nextIndex = (this.currentSlide + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }
  
  goToSlide(index) {
    if (this.isTransitioning || index === this.currentSlide) return;
    
    this.isTransitioning = true;
    
    // Update active states
    this.updateActiveStates(index);
    
    // Preload adjacent images
    this.preloadImage((index + 1) % this.totalSlides);
    if (index > 0) {
      this.preloadImage(index - 1);
    }
    
    this.currentSlide = index;
    
    // Reset transition lock
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);
    
    // Reset autoplay timer
    if (this.autoplay) {
      this.resetAutoplay();
    }
  }
  
  updateActiveStates(index) {
    // Update slides
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
    });
    
    // Update thumbnails
    this.thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('active', i === index);
    });
    
    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });
    
    // Scroll thumbnail into view if needed
    if (this.thumbnails[index]) {
      this.scrollThumbnailIntoView(index);
    }
  }
  
  scrollThumbnailIntoView(index) {
    const thumbnail = this.thumbnails[index];
    const container = this.section.querySelector('.hero-slider__thumbnails');
    
    if (!container) return;
    
    const containerWidth = container.offsetWidth;
    const thumbnailLeft = thumbnail.offsetLeft;
    const thumbnailWidth = thumbnail.offsetWidth;
    const scrollLeft = container.scrollLeft;
    
    // Check if thumbnail is out of view
    if (thumbnailLeft < scrollLeft || thumbnailLeft + thumbnailWidth > scrollLeft + containerWidth) {
      // Scroll to center the thumbnail
      const scrollTo = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2);
      container.scrollTo({
        left: scrollTo,
        behavior: 'smooth'
      });
    }
  }
  
  preloadImage(index) {
    const slide = this.slides[index];
    if (!slide) return;
    
    const images = slide.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      img.loading = 'eager';
    });
  }
  
  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
  }
  
  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
  }
  
  handleSwipe() {
    const swipeThreshold = 50;
    const diff = this.touchStartX - this.touchEndX;
    
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next slide
        this.nextSlide();
      } else {
        // Swipe right - previous slide
        this.previousSlide();
      }
    }
  }
  
  handleKeyboard(e) {
    // Only handle keyboard events when slider is in viewport
    if (!this.isInViewport()) return;
    
    switch(e.key) {
      case 'ArrowLeft':
        this.previousSlide();
        break;
      case 'ArrowRight':
        this.nextSlide();
        break;
    }
  }
  
  isInViewport() {
    const rect = this.slider.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
    );
  }
  
  startAutoplay() {
    if (!this.autoplay) return;
    
    this.pauseAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }
  
  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
  
  resetAutoplay() {
    this.pauseAutoplay();
    this.startAutoplay();
  }
  
  destroy() {
    this.pauseAutoplay();
    // Remove event listeners if needed
  }
}

// Initialize all hero sliders on the page
document.addEventListener('DOMContentLoaded', () => {
  const heroSliders = document.querySelectorAll('.hero-slider-section');
  
  heroSliders.forEach(section => {
    new HeroSlider(section);
  });
});

// Re-initialize on Shopify section reload (Theme Editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  if (section.classList.contains('hero-slider-section')) {
    new HeroSlider(section);
  }
});